import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SiteFooter from "@/components/SiteFooter";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const { forgotPassword } = useAuth();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch {
      // Server returns 200 always; this catch is a safety net
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-foreground/30 glass-strong">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <ThemeToggle />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 sm:px-6 py-10 sm:py-12">
        <div className="w-full max-w-md panel-glass p-6 sm:p-8 md:p-10 relative corner-marks" data-testid="forgot-password-card">
          {!sent ? (
            <>
              <div className="terminal-label mb-2 text-primary">[ ACCOUNT.RECOVER ]</div>
              <h1 className="font-display text-3xl uppercase font-bold mb-2 tracking-tight">Forgot password?</h1>
              <p className="text-sm text-muted-foreground mb-8">
                Enter your email - we&apos;ll send a reset link if an account exists.
              </p>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="terminal-label">Email</label>
                  <input
                    data-testid="forgot-email"
                    type="email"
                    required
                    className="input-base mt-2"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@studio.com"
                  />
                </div>
                <button data-testid="forgot-submit" type="submit" disabled={busy} className="btn-primary w-full mt-6">
                  {busy ? "..." : "Send reset link →"}
                </button>
              </form>
              <div className="mt-6 text-right">
                <Link to="/auth" className="text-xs text-foreground/60 hover:text-primary">Back to log in</Link>
              </div>
            </>
          ) : (
            <div data-testid="forgot-sent">
              <div className="terminal-label mb-3 text-primary">[ CHECK.INBOX ]</div>
              <Mail className="w-10 h-10 text-foreground/70 mb-6" strokeWidth={1.25} />
              <h1 className="serif-mod text-3xl tracking-tight mb-3" style={{ fontWeight: 500 }}>Check your inbox.</h1>
              <p className="text-sm text-foreground/75 mb-3">
                If <span className="font-mono text-primary">{email}</span> matches an account, you&apos;ll get a reset link in a moment. The link expires in 30 minutes.
              </p>
              <p className="text-xs text-foreground/60 mb-6">
                Don&apos;t see it? Check your spam or junk folder - the first email from us sometimes lands there.
              </p>
              <Link to="/auth" className="btn-secondary inline-flex">Back to log in</Link>
            </div>
          )}
        </div>
      </div>
      <SiteFooter variant="site" />
    </div>
  );
}
